import { createContext, useContext } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { IUser, FeatureKey } from '@carrierbridge/shared';
import { isFeatureEnabledForUser } from '@carrierbridge/shared';
import { useMe } from '@/hooks/useAuth';
import { clearSession, getAccessToken } from '@/lib/api';
import { Skeleton } from '@/components/ui/Skeleton';

interface AuthContextValue {
  user: IUser;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuthContext() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used inside AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const token = getAccessToken();
  const { data: user, isLoading, isError } = useMe();
  const location = useLocation();

  // No token → redirect to login
  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Loading user data
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-bg">
        <div className="space-y-3 w-64">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    );
  }

  // Token invalid / error → redirect to login
  if (isError || !user) {
    clearSession();
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <AuthContext.Provider value={{ user }}>
      {children}
    </AuthContext.Provider>
  );
}

/* Écran 403 : remplacer la redirection silencieuse vers l'accueil — un
   utilisateur qui suit un lien vers un module non accessible doit comprendre
   pourquoi il n'y arrive pas, pas être téléporté sans explication. */
function AccessDenied({ reason }: { reason: 'role' | 'feature' }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6 text-center">
      <div>
        <div className="text-lg font-semibold text-text">{t('auth.accessDeniedTitle')}</div>
        <p className="mt-1 max-w-md text-sm text-muted">
          {reason === 'feature'
            ? t('auth.accessDeniedFeature')
            : t('auth.accessDeniedRole')}
        </p>
        <a href="/" className="mt-4 inline-block rounded bg-accent px-4 py-2 text-sm font-medium text-white">
          {t('auth.backToHome')}
        </a>
      </div>
    </div>
  );
}

export function RequireRole({ roles, children }: { roles: string[]; children: React.ReactNode }) {
  const { user } = useAuthContext();
  if (!roles.includes(user.role)) {
    return <AccessDenied reason="role" />;
  }
  return <>{children}</>;
}

export function RequireFeature({
  feature,
  children,
}: {
  feature: FeatureKey;
  children: React.ReactNode;
}) {
  const { user } = useAuthContext();
  if (!isFeatureEnabledForUser(user, feature)) return <AccessDenied reason="feature" />;
  return <>{children}</>;
}
